import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "../cn";

export type NarrativeFigure = {
  label: string;
  value: string;
  tone?: "neutral" | "warn" | "bad" | "ok";
};

export interface NarrativeCardProps {
  variant?: "briefing" | "flag" | "interpretation";
  title: string;
  body: string;
  /** Optional overline / authorial source above the title (e.g. "Oversight note"). */
  eyebrow?: string;
  /** For flag variant: ties the rail + glyph colour to severity. */
  tone?: "warn" | "bad" | "info";
  figures?: NarrativeFigure[];
  cta?: { label: string; href: string };
  className?: string;
}

const figureTone: Record<NonNullable<NarrativeFigure["tone"]>, string> = {
  neutral: "text-ink",
  warn: "text-warnStrong",
  bad: "text-badStrong",
  ok: "text-okStrong",
};

const railTone: Record<"warn" | "bad" | "info", string> = {
  warn: "bg-warn",
  bad: "bg-bad",
  info: "bg-info",
};

export function NarrativeCard({
  variant = "briefing",
  title,
  body,
  eyebrow,
  tone = "warn",
  figures,
  cta,
  className,
}: NarrativeCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-md border border-hairline bg-card p-5 shadow-card",
        variant === "flag" && "border-l-0 pl-5",
        variant === "interpretation" && "bg-subtle/50 shadow-none",
        className,
      )}
    >
      {variant === "flag" && (
        <span className={cn("absolute inset-y-0 left-0 w-0.5 rounded-l-md", railTone[tone])} aria-hidden />
      )}
      {eyebrow && <p className={cn("text-meta text-faint", variant === "flag" && "pl-1.5")}>{eyebrow}</p>}
      <p className={cn("text-sm font-semibold tracking-tight text-ink", eyebrow && "mt-1", variant === "flag" && "pl-1.5")}>
        {title}
      </p>
      <p
        className={cn(
          "mt-1.5 max-w-prose text-sm leading-relaxed text-mute",
          variant === "interpretation" && "leading-snug",
          variant === "flag" && "pl-1.5",
        )}
      >
        {body}
      </p>
      {figures && figures.length > 0 && (
        <div className="mt-4 flex flex-wrap items-stretch gap-x-5 gap-y-2">
          {figures.map((f, i) => (
            <div
              key={f.label}
              className={cn("flex flex-col", i > 0 && "border-l border-hairline pl-5")}
            >
              <span className="text-xs text-faint">{f.label}</span>
              <span className={cn("text-sm font-medium tabular-nums", figureTone[f.tone ?? "neutral"])}>
                {f.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {cta && (
        <Link
          to={cta.href}
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
        >
          {cta.label}
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}
